import React, {
    createContext, useEffect, useReducer, useRef, useState, useMemo,
} from "react";
import calculatePrice from "services/patio-calculator";
import getAdjustedVariants from "@helpers/patio-sort";
import extractGid from "@helpers/extract-gid";
import { MEASUREMENTS_STATE } from "@root/constants/enums";

const initialAttributes = {
    _customProduct: "Yes",
    __basePrice: "",
};

// Accept function actions so callers can compute their partial update from the
// latest state. Without this, rapid successive dispatches that each derive
// from a stale `configuration` snapshot can clobber each other — notably when
// multiple customization sections touch `customization_variant_ids`.
const patioLogic = (state, action) => {
    const delta = typeof action === "function" ? action(state) : action;
    return { ...state, ...delta };
};

export const PatioContext = createContext(null);

const usePatioCalculator = (
    productObject = {},
    product = null,
    variant = null,
    parameters = {},
    customizations = [],
    initialConfiguration = {},
    initialCustomAttributes = {}
) => {
    const [configuration, updateConfiguration] = useReducer(patioLogic, {
        product,
        variant,
        amount: 1,
        ...parameters,
        customization_variant_ids: customizations,
        ...initialConfiguration,
    });

    const [customAttributes, setCustomAttributes] = useState({
        ...initialAttributes,
        ...initialCustomAttributes,
    });

    const [activeCoordinate, setActiveCoordinate] = useState(null);

    const [measurements, setMeasurements] = useState(Object.entries(parameters).reduce((acc, item) => {
        const value = initialConfiguration?.[item[0]];

        acc[item[0]] = {
            value: value ? +value : item[1],
            defaultValue: item[1],
            default: value ? false : true
        }
        return acc;
    }, {}));

    const measurementsState = useMemo(() => {

        const values = Object.values(measurements)

        const allEmpty = values.every(v => v.default === true);
        if (allEmpty) return MEASUREMENTS_STATE.START;

        const allFilled = values.every(v => v.default === false);
        if (allFilled) return MEASUREMENTS_STATE.FINISHED;

        return MEASUREMENTS_STATE.CONTINUE;
    }, [measurements]);


    const [pricesByFabric, setPricesByFabric] = useState({});

    const [priceData, setPriceData] = useState(null);
    const [loading, setLoading] = useState(false);

    // Tracks the most recent fetchPrice invocation so stale calls
    // (whose inner calculatePrice requests got cancelled by latestAsync)
    // don't overwrite fresh results with empty data.
    const fetchPriceCallId = useRef(0);

    const setMeasurement = (key, value) => {
        updateConfiguration({ [key]: value });
        setMeasurements((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                value: !value ? parameters[key] : value,
                default: !value,
            },
        }));
    }

    const fetchPrice = async () => {
        const callId = ++fetchPriceCallId.current;
        setLoading(true);

        const groupedVariants = getAdjustedVariants(productObject)?.groupedByFabric;

        const fetchPrices = groupedVariants?.map((group) => calculatePrice({ ...configuration, variant: +extractGid(group.variants[0].node.id) }));

        // Sign the selected variant; reuse a fabric request only when it priced that same variant
        // (a second call for the same variant would cancel it via latestAsync).
        const sameVariantIndex = groupedVariants?.findIndex((group) => +extractGid(group.variants[0].node.id) === configuration.variant);
        let data = fetchPrices?.[sameVariantIndex] || calculatePrice(configuration);

        const promisedPrices = (await Promise.allSettled([...fetchPrices, data])).map((result) => {
            return result?.status === "fulfilled" ? result?.value : {};
        });

        // Bail out if a newer fetchPrice started after this one — otherwise
        // we'd overwrite the fresh result with empty data from the cancelled
        // latestAsync requests.
        if (callId !== fetchPriceCallId.current) {
            return null;
        }

        data = promisedPrices?.pop();
        setPricesByFabric( (prev) => {
            const newPrices = {};
            groupedVariants?.forEach(async (group, index) => {
                newPrices[group?.fabric] = promisedPrices[index]?.totalPrice?.toString();
            });
            return {
                ...prev,
                ...newPrices,
            };
        });

        setLoading(false);


        setCustomAttributes((prevAttributes) => ({
            ...prevAttributes,
            __basePrice: data?.basePrice?.toString(),
            totalPrice: data?.totalPrice?.toString(),
            _squareFeet: data?.totalSqft,
            _cutPieces: +(data?.cutPieces || 0),
            ...configuration,
        }));
        return data;
    };

    const updatePrice = async () => {
        const result = await fetchPrice();
        if (result === null) return;
        setPriceData(result);
    };

    useEffect(() => {
        (async () => {
            await updatePrice();
        })();
    }, [configuration]);

    return {
        configuration,
        updateConfiguration,
        updatePrice,
        priceData,
        customAttributes,
        setCustomAttributes,
        activeCoordinate,
        setActiveCoordinate,
        loading,
        setMeasurement,
        measurements,
        measurementsState,
        pricesByFabric,
    };
};

const PatioCalculator = ({
    productObject,
    product,
    variant,
    children,
    parameters,
    customizations,
    initialConfiguration,
    initialCustomAttributes,
}) => {
    const calculator = usePatioCalculator(
        productObject,
        product,
        variant,
        parameters,
        customizations,
        initialConfiguration,
        initialCustomAttributes
    );

    return (
        <PatioContext.Provider value={calculator}>
            {children}
        </PatioContext.Provider>
    );
};

export default PatioCalculator;
